import { Box, CircularProgress } from '@mui/material';
import { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { LOGIN_URL, setTokenFromURL, apiFetch } from './utils/auth.js';
import { LicenseInfo } from '@mui/x-license';
import { AllEnterpriseModule, LicenseManager, ModuleRegistry } from "ag-grid-enterprise";

// Lazy load route components
const Quicksignals = lazy(() => import('./Quicksignals.jsx'));

// Regular imports for components used on every route
import { AuthProvider } from './utils/AuthProvider.jsx';
import PersistLogin from './components/commons/PersistLogin';
import Unauthorized from './components/commons/Unauthorized';
import ProtectedRoute from './components/commons/ProtectedRoute';
import { CustomThemeProvider } from './components/commons/ThemeContext.jsx';
import LoadingOverlay from "./components/commons/LoadingOverlay.jsx";
import ErrorBoundary from './components/commons/ErrorBoundary';
import VersionBanner from './components/commons/VersionBanner';

LicenseManager.setLicenseKey(
  "Using_this_{AG_Grid}_Enterprise_key_{AG-079074}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Altop_Energy_LLC}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Galaxy}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Galaxy}_need_to_be_licensed___{Galaxy}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{11_March_2026}____[v3]_[01]_MTc3MzE4NzIwMDAwMA==2dffc0c72de44d5837765b8aa995bc3f"
);
ModuleRegistry.registerModules([AllEnterpriseModule]);
LicenseInfo.setLicenseKey("0d35ec1c3e600510479c8d5df1e8fbf8Tz0xMDI1NjksRT0xNzY0MTk0NzA3MDAwLFM9cHJlbWl1bSxMTT1wZXJwZXR1YWwsUFY9aW5pdGlhbCxLVj0y");

// Loading fallback for lazy-loaded routes
const RouteLoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

function App() {
  const [iso, setIso] = useState("");
  const [mode, setMode] = useState("light");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const { iso, mode } = setTokenFromURL();
    if (iso) {
      setIso(iso);
    }
    // if (mode) {
    //   const defaultMode = localStorage.getItem('DefaultMode')
    //   if (defaultMode) {
    //     setMode(defaultMode)
    //   } else {
    //     localStorage.setItem("DefaultMode", mode)
    //     setMode(mode)
    //   }
    // }
  }, []);
  useEffect(() => {
    const { token, iso: urlIso, mode: urlMode } = setTokenFromURL();

    // Apply the theme IMMEDIATELY from the launch URL (?mode=, set by the dashboard) or the last-known
    // cached theme, so the app paints in the correct theme with no network wait and no flash. Then stop
    // blocking first paint — login/apps/ (drawer app-list + theme reconcile) loads in the BACKGROUND
    // since on a secondary app that data feeds the nav drawer, which the user rarely touches on load.
    const cachedTheme = localStorage.getItem('altop_theme');
    if (urlMode) {
      setMode(urlMode);
    } else if (cachedTheme) {
      setMode(cachedTheme);
    }
    setLoading(false);

    // Background: refresh preferences without blocking render.
    (async () => {
      try {
        const response = await apiFetch('login/apps/');
        if (response) {
          const data = await response.json();
          if (data.defaultTheme) {
            setMode(data.defaultTheme);
            try { localStorage.setItem('altop_theme', data.defaultTheme); } catch (e) { /* ignore */ }
          }
        }
      } catch (error) {
        console.error('Failed to fetch user preferences:', error);
      }
    })();
  }, []);
  if (loading) {
    return <LoadingOverlay loading={loading} setLoading={setLoading} />;
  }
  return (
    <ErrorBoundary fullPage errorTitle="Application Error" errorMessage="The application encountered an unexpected error.">
      <AuthProvider>
        <CustomThemeProvider mode={mode} setMode={setMode}>
          <Router>
            <Box>
              <VersionBanner appName="QuickSignals" />
              <Suspense fallback={<RouteLoadingFallback />}>
                <Routes>
                  <Route element={<PersistLogin />}>
                    <Route path={LOGIN_URL} />
                    <Route element={<ProtectedRoute />}>
                      <Route path="/" element={
                        <ErrorBoundary fullPage errorTitle="Quick Signals Error" errorMessage="Unable to load Quick Signals module.">
                          <Quicksignals iso={iso} />
                        </ErrorBoundary>
                      } />
                      <Route path="*" element={
                        <ErrorBoundary fullPage errorTitle="Quick Signals Error" errorMessage="Unable to load Quick Signals module.">
                          <Quicksignals iso={iso} />
                        </ErrorBoundary>
                      } />
                    </Route>
                    <Route path='/unauthorized' element={<Unauthorized />} />
                  </Route>
                </Routes>
              </Suspense>
            </Box>
          </Router>
        </CustomThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;